/**
 * Sentry error tracking for @wayai/cli
 *
 * Production-only: skips initialization when running from source (development).
 * Opt-out: set WAYAI_TELEMETRY_DISABLED=1 to disable.
 */

import * as Sentry from '@sentry/node';
import { readFileSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import { homedir, platform, arch } from 'node:os';
import { maskSecretsInMessage } from './mask-secrets.js';
import { isNetworkError } from './network-error.js';

const HARDCODED_DSN = 'https://ffe500121277babcabf32e5868f00f9e@o4510669417807872.ingest.us.sentry.io/4510960145465344';

let initialized = false;

/**
 * Source/dev fallback for the CLI version. Correct ONLY when running unbundled — two `..` from
 * `src/lib/` reaches the package root. In the published binary every module collapses into
 * `dist/index.js` (tsup `bundle: true`), where `import.meta.url` resolves to the bundle and two `..`
 * overshoots to a nonexistent `package.json` → `'0.0.0'`. So production MUST inject the version
 * resolved once at the entry (`index.ts`, one `..` — correct in both layouts), carried by
 * `initSentry`'s `version` param; this fallback only runs from source, where it is correct.
 */
function getVersion(): string {
  try {
    const __dirname = dirname(fileURLToPath(import.meta.url));
    const pkg = JSON.parse(readFileSync(join(__dirname, '..', '..', 'package.json'), 'utf-8'));
    return pkg.version || '0.0.0';
  } catch {
    return '0.0.0';
  }
}

/**
 * Detect if running from npm install (production) vs from source (development).
 * When installed via npm, import.meta.url path contains 'node_modules'.
 * SENTRY_DSN env var overrides this check for testing.
 */
function isInstalledPackage(): boolean {
  try {
    return fileURLToPath(import.meta.url).includes('node_modules');
  } catch {
    return false;
  }
}

/**
 * Initialize Sentry for the CLI.
 *
 * @param command - The CLI command being run (e.g., 'push', 'pull')
 * @param version - The CLI version resolved at the entry (`index.ts`). Authoritative in the bundled
 *   binary; falls back to `getVersion()` (source-only) when omitted.
 */
export function initSentry(command?: string, version?: string): void {
  if (initialized) return;

  // Opt-out
  if (process.env.WAYAI_TELEMETRY_DISABLED === '1') return;

  const dsn = process.env.SENTRY_DSN || HARDCODED_DSN;

  // Skip when running from source (dev), unless SENTRY_DSN is explicitly set
  if (!process.env.SENTRY_DSN && !isInstalledPackage()) return;

  const resolvedVersion = version ?? getVersion();

  Sentry.init({
    dsn,
    tracesSampleRate: 0,
    release: `wayai-cli@${resolvedVersion}`,
    environment: 'production',
    defaultIntegrations: false,
    beforeSend(event) {
      // Sanitize home directory paths in stack frames
      const home = homedir();
      if (event.exception?.values) {
        for (const value of event.exception.values) {
          if (home && value.stacktrace?.frames) {
            for (const frame of value.stacktrace.frames) {
              if (frame.filename && frame.filename.includes(home)) {
                frame.filename = frame.filename.replace(home, '~');
              }
            }
          }
          // Backstop: scrub token-shaped secrets from the exception message.
          // ApiError already redacts its body at construction; this covers any
          // other error whose message embedded a credential.
          if (value.value) value.value = maskSecretsInMessage(value.value);
        }
      }
      if (event.breadcrumbs) {
        for (const crumb of event.breadcrumbs) {
          if (crumb.message) crumb.message = maskSecretsInMessage(crumb.message);
        }
      }
      // Also scrub string leaves of `extra` (set via captureException context) —
      // the remaining text channel a future caller could route a token through.
      if (event.extra) {
        for (const [key, val] of Object.entries(event.extra)) {
          if (typeof val === 'string') event.extra[key] = maskSecretsInMessage(val);
        }
      }
      return event;
    },
  });

  // Set default tags
  Sentry.setTag('command', command || 'unknown');
  Sentry.setTag('node_version', process.version);
  Sentry.setTag('os_platform', platform());
  Sentry.setTag('os_arch', arch());

  initialized = true;
}

/**
 * Whether an error should be reported to Sentry.
 *
 * Skipped: expected API errors (4xx — `isExpected`), backend-flagged retryable
 * transients (`isRetryable`, e.g. a `CLICKHOUSE_COLD` cold-start that outlived
 * the ApiClient's bounded retry), and host-network failures (`fetch failed` —
 * the user's offline/DNS/connection problem, which WayAI can't fix; gh #2933).
 * None is a bug — this mirrors the backend's `SENTRY_SKIP_CODES`, which already
 * drops `CLICKHOUSE_COLD` (gh #2883).
 */
export function shouldReportToSentry(error: unknown): boolean {
  if (isNetworkError(error)) return false;
  if (error instanceof Error) {
    const e = error as { isExpected?: unknown; isRetryable?: unknown };
    if (e.isExpected === true || e.isRetryable === true) return false;
  }
  return true;
}

/**
 * Capture an exception to Sentry.
 */
export function captureException(
  error: unknown,
  context?: { tags?: Record<string, string>; extra?: Record<string, unknown> }
): void {
  if (!initialized) return;

  Sentry.withScope((scope) => {
    if (context?.tags) {
      for (const [key, value] of Object.entries(context.tags)) {
        scope.setTag(key, value);
      }
    }
    if (context?.extra) {
      for (const [key, value] of Object.entries(context.extra)) {
        scope.setExtra(key, value);
      }
    }
    Sentry.captureException(error);
  });
}

/**
 * Add a breadcrumb for API requests.
 */
export function addApiBreadcrumb(method: string, path: string): void {
  if (!initialized) return;

  Sentry.addBreadcrumb({
    category: 'http',
    message: `${method} ${path}`,
    level: 'info',
  });
}

/**
 * Set the Sentry user from a way_ token or JWT access token.
 * For way_ tokens, uses a hash prefix as the user identifier.
 * For JWTs (legacy), decodes the payload to extract the `sub` claim.
 */
export function setSentryUser(accessToken: string): void {
  if (!initialized) return;

  try {
    if (accessToken.startsWith('way_')) {
      // way_ tokens are opaque — use a truncated hash as user identifier
      const hash = require('node:crypto')
        .createHash('sha256')
        .update(accessToken)
        .digest('hex')
        .slice(0, 16);
      Sentry.setUser({ id: `token:${hash}` });
      return;
    }

    // Legacy JWT path
    const payloadBase64 = accessToken.split('.')[1];
    if (!payloadBase64) return;

    const payload = JSON.parse(Buffer.from(payloadBase64, 'base64url').toString());
    if (payload.sub) {
      Sentry.setUser({ id: payload.sub });
    }
  } catch {
    // Silently ignore malformed tokens — user context is best-effort
  }
}

/**
 * Flush and close Sentry. Must be awaited before process.exit().
 */
export async function closeSentry(): Promise<void> {
  if (!initialized) return;
  await Sentry.close(2000);
  initialized = false;
}
